import { useState } from 'react';

import type {
  BroadcastContent,
  DelayBroadcastPattern,
  SelfTrainDelayProperties,
} from '../../../api/types';
import { GRANULARITY_DEFAULT_SSML } from '../../../utils/broadcastContents';
import { createDefaultPattern } from '../logic';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import { PlaceholderLegend } from '../parts/PlaceholderLegend';
import { SELF_TRAIN_DELAY_PLACEHOLDERS } from '../placeholders';
import { deriveSampleDelaySeconds } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { DelayPatternRow } from './DelayPatternRow';

export function SelfTrainDelayFields({
  props,
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  onChange,
}: {
  props: Partial<SelfTrainDelayProperties>;
  /**
   * プレビュー対象の放送コンテンツ (Issue #439)。
   * サーバ側でこの内容をレンダリングして実放送と同一の文面を合成する。
   */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  onChange: (props: SelfTrainDelayProperties) => void;
}): React.JSX.Element {
  /*
   * プレビューで「実際に採用された遅延パターン行」の index (Issue #439)。
   *
   * サーバの `select_delay_pattern()` が選んだ行をそのまま受け取り、
   * 表の該当行をハイライトする。これにより「境界値 (4分/5分) でどのパターンが鳴るか」を
   * 保存もデプロイもせずに目で確認できる。
   */
  const [matchedPatternIndex, setMatchedPatternIndex] = useState<number | null>(null);
  // 放送粒度毎の行は固定ではなく、ユーザーが「＋」「−」で追加・削除できる。
  // 既存データをそのまま行として表示し、空の場合は最低 1 行を表示する。
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0 ? existingPatterns : [createDefaultPattern()];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    const current = next[index]!;
    // 粒度プルダウンが変更された場合は、対応する粒度のデフォルト SSML 文言を
    // ssml_template に再設定する (ユーザ要望: 「粒度を変えたら元々のデフォルト値を
    // 再設定して」)。ユーザがカスタム編集していた場合も上書きされるが、
    // SSML 欄をクリアすればゼロベース入力に戻せるので問題ない。
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    onChange({ delay_patterns: next });
  };

  /** index の直後に新しい行を挿入する */
  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, createDefaultPattern());
    onChange({ delay_patterns: next });
  };

  /** index の行を削除する (最後の 1 行は削除しない) */
  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    onChange({ delay_patterns: next });
  };

  return (
    <div className="contents-fields">
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              // Issue #439: 行ごとに「▶ この文面を試聴」を出す。
              // サンプル遅延はその行の `delay_minutes_from` から自動導出するので、
              // ユーザーは追加入力なしで「その行が選ばれる遅延」で試聴できる。
              previewSlot={
                content ? (
                  <AudioPreviewField
                    content={content}
                    parentVoiceId={parentVoiceId}
                    playbackRate={playbackRate}
                    onPreviewSsml={onPreviewSsml}
                    testIdSuffix={`self-train-delay-${contentIndex}-${i}`}
                    label="▶ この文面を試聴"
                    placeholders={SELF_TRAIN_DELAY_PLACEHOLDERS}
                    sampleOverrides={{ delaySeconds: deriveSampleDelaySeconds(pattern) }}
                    disabled={(pattern.ssml_template ?? '').trim() === ''}
                    disabledReason="SSML を入力すると試聴できます"
                    onMatchedPatternIndexChange={setMatchedPatternIndex}
                  />
                ) : null
              }
              // サーバが選んだ行をハイライトして「どの行が鳴ったか」を可視化する。
              highlighted={matchedPatternIndex === i}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        各行の「＋」で行を追加、「−」で行を削除できます。 SSML
        欄は必須項目です。各放送粒度のデフォルト文言があらかじめ入力されているので、必要に応じて書き換えてください。
      </p>
      {/*
        放送パターン (SSML テンプレート) で使えるプレースホルダの凡例。
        ユーザは説明文を「読む」というより、SSML 欄に `{delay_minutes_1}` 等を
        コピペして書き始める用途で凡例を使うので、トークンを縦に並べて
        クリック 1 回でクリップボードにコピーできる UI にする。
      */}
      <PlaceholderLegend
        testId="self-train-delay-route-hint"
        items={SELF_TRAIN_DELAY_PLACEHOLDERS}
      />
    </div>
  );
}
